import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { ChevronDown, Search, FileQuestion } from "lucide-react";
import storage from "../../../../Storage";
import C from "../../../../Constant";
import coins from "../../../coins";
import socket from "../../../../Socket";
import { Dropdown } from "react-bootstrap";
import {
  __, wait, decode, encode, forceSatoshiFormat, Event,
  isMobile,
  sendNotfication,
  getUID,
} from '../../../../Helper';
import axios from 'axios';

/* ----------------- Safe helpers (guards) ----------------- */
const networkCoinMap = {
  ETH: 'eth',
  TRX: 'trx',
  BSC: 'bsc',
  // extend as needed
};
const coinsList = (Array.isArray(coins) ? coins : []); // fallback; you used coinsList before

/* ----------------- Styled components ----------------- */
const WithdrawContainer = styled.div`
  margin: 0 auto;
  padding: 24px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;
const Row1 = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
`;
const Label1 = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  ${props => props.isGreen && `
    color: #20EE89;
    font-size: 16px;
    font-weight: 500;
  `}
  ${props => props.isGray && `
    color: #9ba1a6;
    font-size: 14px;
  `}
`;
const Amount = styled.div`
  ${props => props.isGreen && `color: #20EE89;`}
  font-size: ${props => props.large ? '16px' : '14px'};
  font-weight: ${props => props.bold ? '600' : '400'};
`;
const IconButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.7;
  &:hover { opacity: 1; }
`;
const Container = styled.div`
  padding: 8px;
  color: #ffffff;
`;
const DropdownContainer = styled.div`
  position: relative;
  margin-bottom: 16px;
  flex:1;
`;
const DropdownButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding:6px 10px;
  background: rgb(30, 35, 40);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  color: #ffffff;
  cursor: pointer;
  font-size: 16px;
`;
const DropdownPanel = styled.div`
  position: absolute;
  top: 100%;
  left: 0; right: 0;
  background: rgb(30, 35, 40);
  border: 1px solid rgb(58 65 66);
  border-radius: 8px;
  margin-top: 4px;
  max-height: 400px;
  overflow-y: auto;
  z-index: 900;
  padding:10px 12px;
`;
const SearchContainer = styled.div`
  position: relative;
  padding:0px;
  border-radius: 8px;
  border: 1px solid rgb(58 65 66);
  margin-top:12px;
`;
const SearchInput = styled.input`
  width: 100%;
  padding: 6px 12px 6px 40px;
  background: transparent;
  border: none;
  border-radius: 8px;
  color: #ffffff;
  font-size: 16px;
  &::placeholder { color: #fff; opacity:0.6; }
`;
const Input = styled.input`
  width:100%;
  padding:6px 10px;
  background: rgb(30, 35, 40);
  border: 1px solid rgba(255, 255, 255, 0.05);
  margin-bottom:12px;
  border-radius: 8px;
  color: #ffffff;
  font-size: 14px;
  &::placeholder { color: #fff; opacity:0.4; }
`;
const DepositButton = styled.div`
  margin-top: auto;
  flex:1;
  padding:7px 9px;
  display:flex;
  justify-content:center;
  align-items:center;
  background:#FF5C5C;
  color: black !important;
  border: none;
  border-radius: 0.5rem;
  font-weight: 600;
  cursor: pointer;
  &:hover { opacity: 0.9; }
`;
const SearchIcon = styled.div`
  position: absolute;
  left: 14px;
  top: 49%;
  transform: translateY(-50%);
  color: #fff;
`;
const CoinOption = styled.div`
  display: flex;
  align-items: center;
  padding: 14px;
  cursor: pointer;
  border-radius:12px;
  &:hover { background: rgba(0, 0, 0, 0.25); }
  ${props => props.isSelected && `background: rgba(0, 0, 0, 0.25);`}
`;
const CoinImage = styled.img`
  width: 24px; height: 24px; border-radius: 50%; margin-right: 12px;
`;
const CoinName = styled.div`
  font-size: 16px; font-weight: bold;
`;
const CoinBalance = styled.span`
  margin-left: auto; color: #8E9293; display:none;
`;
const WarningContainer = styled.div`
  background-color: rgba(52, 199, 89, 0.1);
  border-radius: 8px;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 16px auto;
  margin-bottom: 16px;
`;
const WarningText = styled.span`
  color: #fff;
  font-size: 13px;
  font-weight: 500;
`;
const WarningTextGreen = styled.span`
  color: #34C759;
  font-size: 14px;
  font-weight: 500;
`;

/* Modal & slider styled */
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.55);
  display:flex;
  align-items:center;
  justify-content:center;
  z-index: 2200;
`;
const ModalCard = styled.div`
  background: #1E2329;
  color: #fff;
  border-radius: 12px;
  padding: 20px;
  width: 480px;
  max-width: 95%;
  box-shadow: 0 10px 30px rgba(0,0,0,0.6);
`;
const ModalHeader = styled.div`
  display:flex;
  align-items:center;
  justify-content:space-between;
`;
const ModalTitle = styled.h3`
  margin:0;
  font-size:18px;
`;
const ModalBody = styled.div`
  margin-top: 14px;
`;
const ProgressTrack = styled.div`
  height: 12px;
  border-radius: 999px;
  background: #2b2f32;
  overflow: hidden;
  position: relative;
`;
const ProgressFill = styled.div`
  height: 100%;
  width: ${props => props.widthPercent}%;
  transition: width 400ms cubic-bezier(.22,.9,.34,1);
  background: linear-gradient(90deg, #FFD54F, #FFB300);
  box-shadow: 0 6px 18px rgba(255, 183, 77, 0.18);
`;

/* ----------------- Component ----------------- */
const SkylaWithdraw = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [chainName, setChainName] = useState("");
  const [credits, setCredits] = useState({});
  const [selectedCoin, setSelectedCoin] = useState(storage.getKey('coin') || "INR");
  const [bankName, setBankName] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [accountHolderName, setAccountHolderName] = useState("");
  const [state, setState] = useState({
    currentCoin: storage.getKey('coin') || "MVR",
    credits: {},
    list: [],
    content: [],
    wallet: "btc",
    deposit: true,
    slide: false,
    height: 573,
    margin: "mt-1",
    amount: "",
    coinId: "",
    networks: [],
    selectedNetwork: "",
    qrString: "",
    coinName: "",
    logoUrl: "",
    isLoading: false,
    isCopied: false,
    depositAddress: "",
    depositAmount: "",
    showDepositDetails: false
  });
  const [withdrawAddress, setWithdrawAddress] = useState("");
  const [withdrawAddressError, setWithdrawAddressError] = useState("");
  const [error3, setError3] = useState("");
  const [final, setFinal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [placeholder, setPlaceholder] = useState("");
  const [showTwoFactorInput, setShowTwoFactorInput] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [iswithdraw, setiswithdraw]= useState(true);

  // New states for eligibility check + wager modal
  const [eligible, setEligible] = useState(true); // default true until checked
  const [wagerInfo, setWagerInfo] = useState(null);
  const [checkingEligibility, setCheckingEligibility] = useState(false);
  const [showWagerModal, setShowWagerModal] = useState(false);
  const [progressPercent, setProgressPercent] = useState(0); // 0..100

  const initialCoinRef = useRef(storage.getKey('coin') || "INR");

  /* ----------------- Helpers ----------------- */
  const isFiatCurrency = useCallback(() => {
    const fiatCurrencies = ['INR', 'MVR', 'PKR', 'NPR', 'AED', 'SC', 'BDT'];
    return fiatCurrencies.includes(selectedCoin);
  }, [selectedCoin]);

  const getCoinNameByNetwork = useCallback((network, coinInfo, defaultCoin) => {
    if (networkCoinMap[network]) return networkCoinMap[network];
    if (coinInfo && coinInfo.id) return coinInfo.id;
    return defaultCoin.toLowerCase();
  }, []);

  const getCreditAndCoins = useCallback((data) => {
    const { credit } = data;
    setState(prev => ({ ...prev, credits: credit }));
    setUpCoins(credit);
  }, []);

  const setUpCoins = useCallback((creditsFromState) => {
    if (isMobile()) {
      setState(prev => ({ ...prev, height: 685, margin: "mt-1" }));
    }
    const newList = coins.reduce((acc, item, i) => {
      if (item.preffix === "NC") return acc;
      const credit = forceSatoshiFormat(
        (creditsFromState || state.credits)[__.lowerCase(item.preffix)]
      );
      const listItem = (
        <Dropdown.Item
          key={__.toString(i)}
          as="button"
          className="animated fadeIn"
          onClick={() => loadCoinId(item.preffix)}
        >
          <span>
            <img
              src={`/assets/images/${item.image}`}
              className="img-fluid mini-coin mr-1"
              alt="Coin"
            />
            {item.preffix}
          </span>
        </Dropdown.Item>
      );
      return [...acc, listItem];
    }, []);
    setState(prev => ({ ...prev, list: newList }));
  }, [state.credits]);

  const showArchive = useCallback(() => {
    if (state.slide) Event.emit("deposit_archive_back");
    setState(prev => ({ ...prev, slide: !prev.slide }));
    wait(300).then(() => setState(prev => ({ ...prev, deposit: !prev.deposit })));
    wait(300).then(() => loadCoinId("BTC"));
  }, [state.slide]);

  const loadCoinId = useCallback(async (coinSym) => {
    if (['INR','MVR','PKR','AED'].includes(coinSym.toUpperCase())) {
      return;
    }
    setState(prev => ({
      ...prev,
      currentCoin: coinSym, coinId: "", networks: [],
      selectedNetwork: "", isLoading: true, coinName: "", logoUrl: ""
    }));
    try {
      const token = storage.getKey('token');
      if (!token) {
        setState(prev => ({ ...prev, isLoading: false }));
        return;
      }
      const response = await axios.post(
        'https://api.ibitplay.com/getCoinDetails',
        { symbol: coinSym.toUpperCase() },
        { headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` } }
      );
      const { coinId, networks, coinFullName, logoUrl } = response.data || {};
      let networksList = networks ? Object.keys(networks) : [];
      const coinInfo = coinsList.find(item => item.symbol === coinSym.toUpperCase());
      let selectedNetwork = networksList[0] || "";
      let coinName = coinInfo ? coinInfo.id : coinSym.toLowerCase();
      if (coinSym.toUpperCase() === 'USDT') {
        networksList = ['ETH', 'TRX', 'BSC'];
      }
      coinName = getCoinNameByNetwork(selectedNetwork, coinInfo, coinSym);
      setState(prev => ({
        ...prev,
        coinId, networks: networksList, selectedNetwork,
        isLoading: false, coinName, logoUrl
      }));
    } catch (error) {
      console.error("Error fetching coin details:", error);
      setState(prev => ({ ...prev, isLoading: false }));
    }
  }, [getCoinNameByNetwork]);

  /* ----------------- USER_INFO stable handler ----------------- */
  const handleUserInfo = useCallback((raw) => {
    const data = decode(raw);
    console.log("USER_INFO data:", data);
    if (data.withdraw) setiswithdraw(true);
    else setiswithdraw(false);
  }, []);

  /* ----------------- 2FA ----------------- */
  const check2FAStatus = async () => {
    try {
      const token = storage.getKey('token');
      const res = await axios.get(`https://api.ibitplay.com/2fa/status/${getUID()}`, {
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
      });
      return res.data.isEnabled;
    } catch {
      return false;
    }
  };

  /* ----------------- ELIGIBILITY CHECK ----------------- */
  const checkWagerEligibility = useCallback(async () => {
    const token = storage.getKey('token');
    const uid = getUID();
    if (!token || !uid) {
      // default to disabled if missing auth
      setEligible(false);
      return;
    }
    setCheckingEligibility(true);
    try {
      const url = `https://api.ibitplay.com/check-wager/${uid}`;
      const res = await axios.get(url, {
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
      });
      const data = res.data || {};
      setWagerInfo(data);
      setEligible(Boolean(data.eligible));
    } catch (err) {
      console.error("Failed to check wager eligibility:", err);
      // On error, safe default: disable withdrawals
      setEligible(false);
    } finally {
      setCheckingEligibility(false);
    }
  }, []);

  /* ----------------- Submit handlers ----------------- */
  const processFiatWithdrawal = async () => {
    if (!state.amount || parseFloat(state.amount) <= 0) {
      sendNotfication("Please enter a valid amount", "error", "top-right");
      return;
    }
    if (!accountHolderName) {
      sendNotfication("Account holder name is required", "error", "top-right");
      return;
    }
    if (!bankName || !accountNumber) {
      sendNotfication("Bank details are required for bank transfers", "error", "top-right");
      return;
    }

    setState(prev => ({ ...prev, isLoading: true }));
    const payload = {
      uid: getUID().toString(),
      amount: state.amount,
      currency: selectedCoin,
      withdrawal_type: 'bank',
      bank_name: bankName,
      account_number: accountNumber,
      account_holder_name: accountHolderName
    };
    const token = storage.getKey('token');
    try {
      const response = await axios.post('https://api.ibitplay.com/createFiatWithdrawal', payload, {
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
      });
      if (response.data) {
        sendNotfication("Withdrawal request submitted successfully", "success", "top-right");
        setState(prev => ({ ...prev, amount: "" }));
        setBankName(""); setAccountNumber(""); setAccountHolderName("");
        setTimeout(() => window.location.reload(), 2000);
      } else {
        sendNotfication("Failed to submit withdrawal request", "error", "top-right");
      }
    } catch (error) {
      sendNotfication(error.response?.data?.error || "Error processing withdrawal", "error", "top-right");
    } finally {
      setState(prev => ({ ...prev, isLoading: false }));
    }
  };

  const submitFormCrypto = (e) => {
    if (e) e.preventDefault();
    if (!withdrawAddress.trim()) {
      setWithdrawAddressError('Withdraw address is required');
      return;
    } else setWithdrawAddressError('');
    if (!chainName.trim()) {
      sendNotfication("Chain required", "error", "top-right");
      return;
    }
    if (!state.amount || parseFloat(state.amount) <= 0) {
      setError3("Please enter a valid amount");
      return;
    }
    const fee = 0.01 * parseFloat(state.amount);
    const finalAmount = parseFloat(state.amount) - fee;
    if (finalAmount <= 0) {
      setError3("Amount after fee must be greater than 0");
      return;
    }
    const coin_info = coins.find(item => item.preffix.toLowerCase() === selectedCoin.toLowerCase());
    if (coin_info && finalAmount < Number(coin_info.min)) {
      setError3(`Minimum withdrawal amount is ${coin_info.min} ${selectedCoin}`);
      return;
    }
    if (!final) { setFinal(true); return; }

    setLoading(true);
    setTimeout(() => {
      socket.emit(
        C.SUBMIT_NEW_WITHDRAWL,
        encode({
          coin: selectedCoin,
          wallet: withdrawAddress,
          amount: state.amount,
          immed: fee,
          password: password,
          chain: chainName,
          network: state.selectedNetwork
        })
      );
      setState(prev => ({ ...prev, amount: "" }));
      setWithdrawAddress(""); setPassword(""); setPasswordError(""); setFinal(false); setLoading(false);
      sendNotfication("Withdrawal request submitted successfully", "success", "top-right");
      setTimeout(() => window.location.reload(), 2000);
    }, 500);
  };

  const handleConfirmWithdraw = () => {
    if (!password.trim()) {
      setPasswordError("Password is required");
      return;
    }
    submitFormCrypto();
  };

  const handleTwoFactorVerification = async () => {
    if (!twoFactorCode.trim()) {
      sendNotfication('Please enter verification code', 'error', 'top-right');
      return;
    }
    try {
      const response = await axios.post('https://api.ibitplay.com/2fa/verify', {
        uid: getUID(), token: twoFactorCode
      });
      if (response.data.success) {
        setShowTwoFactorInput(false);
        setTwoFactorCode('');
        if (isFiatCurrency()) processFiatWithdrawal();
        else submitFormCrypto();
      } else {
        sendNotfication('Invalid verification code', 'error', 'top-right');
      }
    } catch {
      sendNotfication('Failed to verify code', 'error', 'top-right');
    }
  };

  // Guarded submit: check eligibility first (original)
  const handleSubmit = async () => {
    if (!eligible) {
      sendNotfication("Withdrawals are temporarily disabled for your account. Please contact support or check wagering requirements.", "error", "top-right");
      return;
    }
    if (error3) return;
    // If you want 2FA gate here, uncomment below:
    // const twoFA = await check2FAStatus();
    // if (twoFA) { setShowTwoFactorInput(true); return; }
    if (isFiatCurrency()) processFiatWithdrawal();
    else submitFormCrypto();
  };

  const handleCancelWithdraw = () => setFinal(false);

  /* ----------------- New: button click that always allows click and shows modal if not eligible ----------------- */
  const onWithdrawButtonClick = (e) => {
    // prevent default if inside a form
    e?.preventDefault?.();
    // if checkingEligibility or loading, give quick notification and bail
    if (checkingEligibility || loading) {
      // small feedback
      sendNotfication("Please wait...", "info", "top-right");
      return;
    }
    if (eligible) {
      // proceed with original flow
      handleSubmit();
    } else {
      // open wager-requirement modal
      setShowWagerModal(true);

      // animate slider (compute percent)
      const current = Number(wagerInfo?.wager || 0);
      const required = Number(wagerInfo?.required || 0);
      let pct = 0;
      if (required > 0) pct = Math.max(0, Math.min(100, Math.round((current / required) * 100)));
      // animate from 0 to pct
      setProgressPercent(0);
      const start = performance.now();
      const duration = 600;
      const animate = (ts) => {
        const t = Math.min(1, (ts - start) / duration);
        const ease = 1 - Math.pow(1 - t, 3); // easeOutCubic
        setProgressPercent(Math.round(ease * pct));
        if (t < 1) requestAnimationFrame(animate);
      };
      requestAnimationFrame(animate);
    }
  };

  const closeWagerModal = () => setShowWagerModal(false);

  const onModalDepositCTA = () => {
    // close modal and emit event to open deposit flow (replace with your actual deposit handler)
    setShowWagerModal(false);
    if (Event && Event.emit) Event.emit('open_deposit');
    // you could also navigate or open a deposit modal here
  };

  /* ----------------- Render helpers ----------------- */
  const renderCryptoForm = () => (
    <div style={{ width: "100%", marginTop: "16px" }}>
      <div style={{ color: "#B3BEC1", margin: "4px 0" }}>
        Withdrawal Address ({selectedCoin})
      </div>
      <Input
        value={withdrawAddress}
        onChange={(e) => setWithdrawAddress(e.target.value)}
        placeholder={`Enter your ${selectedCoin} wallet address`}
      />
      {withdrawAddressError && (
        <div style={{ color: "red", fontSize: "12px", marginTop: "4px" }}>
          {withdrawAddressError}
        </div>
      )}
      {error3 && (
        <div style={{ color: "red", fontSize: "12px", marginTop: "4px" }}>
          {error3}
        </div>
      )}
      <div style={{ color: "#B3BEC1", margin: "4px 0" }}>
        Chain Name
      </div>
      <Input
        value={chainName}
        onChange={(e) => setChainName(e.target.value)}
        placeholder={`Enter your ${selectedCoin} chain / network name`}
      />
    </div>
  );

  const renderINROptions = () => (
    <div style={{ marginTop: "16px", marginBottom: "16px" }}>
      <div style={{ color: "#B3BEC1", margin: "4px 0" }}>Withdrawal Method</div>
      <div style={{ display: "flex", gap: "10px" }}>
        <button
          style={{
            backgroundColor: "#FF5C5C",
            border: "none",
            padding: "8px 16px",
            borderRadius: "4px",
            color: "white",
            cursor: "default"
          }}
        >
          Bank Transfer
        </button>
      </div>
    </div>
  );

  const renderBankForm = () => (
    <div style={{ marginTop: "20px" }}>
      {(selectedCoin === 'INR' || selectedCoin === 'BDT') && renderINROptions()}
      <div style={{ color: "#B3BEC1", margin: "4px 0" }}>Account Holder Name</div>
      <Input
        value={accountHolderName}
        onChange={(e) => setAccountHolderName(e.target.value)}
        placeholder="Enter Account Holder Name"
      />
      <div style={{ color: "#B3BEC1", margin: "10px 0 4px 0" }}>Bank Name</div>
      <Input
        value={bankName}
        onChange={(e) => setBankName(e.target.value)}
        placeholder="Enter Bank Name"
      />
      <div style={{ color: "#B3BEC1", margin: "10px 0 4px 0" }}>Account Number</div>
      <Input
        value={accountNumber}
        onChange={(e) => setAccountNumber(e.target.value)}
        placeholder="Enter Account Number"
      />
    </div>
  );

  /* ----------------- Effects ----------------- */

  // Mount-only: set up USER_INFO listener (stable) and clean up symmetrically
  useEffect(() => {
    socket.on(C.USER_INFO, handleUserInfo);
    return () => socket.off(C.USER_INFO, handleUserInfo);
  }, [handleUserInfo]);

  // Mount-only: after connect/auth, request USER_INFO once (and also initial CREDIT with token)
  useEffect(() => {
    const token = storage.getKey('token');
    if (!token) return;

    const emitAuthStuff = () => {
      socket.emit(C.CREDIT, encode({ token, coin: initialCoinRef.current }));
      socket.emit(C.USER_INFO, encode({ uid: getUID(), token, action: 'get' }));
    };

    if (socket.connected) {
      emitAuthStuff();
    } else {
      const onConnect = () => {
        emitAuthStuff();
        socket.off('connect', onConnect);
      };
      socket.on('connect', onConnect);
      return () => socket.off('connect', onConnect);
    }
  }, []);

  // When selectedCoin changes: refresh CREDIT (with token), DO NOT touch USER_INFO here
  useEffect(() => {
    const token = storage.getKey('token');
    if (token) {
      socket.emit(C.CREDIT, encode({ token, coin: selectedCoin }));
    }

    const handleCreditData = (data) => {
      const decodedData = decode(data);
      const { credit } = decodedData;
      setCredits(credit);
      const currentCoin = __.lowerCase(selectedCoin);
      const currentCredit = forceSatoshiFormat(credit[currentCoin]);
      storage.setKey('credit', currentCredit);
    };

    const handleCreditUpdate = (data) => {
      const decodedData = decode(data);
      const { value, coin: updatedCoin } = decodedData;
      const formattedValue = forceSatoshiFormat(value);
      setCredits(prev => ({ ...prev, [__.lowerCase(updatedCoin)]: value }));
      if (__.lowerCase(updatedCoin) === __.lowerCase(selectedCoin)) {
        storage.setKey('credit', formattedValue);
      }
    };

    socket.on(C.CREDIT, handleCreditData);
    socket.on(C.UPDATE_CREDIT, handleCreditUpdate);

    return () => {
      socket.off(C.CREDIT, handleCreditData);
      socket.off(C.UPDATE_CREDIT, handleCreditUpdate);
      // IMPORTANT: do NOT call socket.off(C.USER_INFO) here
    };
  }, [selectedCoin]);

  // Placeholder text depending on coin
  useEffect(() => {
    if (selectedCoin === "MVR") {
      setPlaceholder("Enter Withdraw Amount (Minmim 500MVR and Maximum 50,000MVR)");
    } else {
      setPlaceholder("Enter Withdraw Amount");
    }
  }, [selectedCoin]);

  // Initial setup (credit list + coin details)
  useEffect(() => {
    const token = storage.getKey('token');
    socket.emit(C.CREDIT, encode({ token, coin: initialCoinRef.current }));
    const onCredit = (data) => getCreditAndCoins(decode(data));
    socket.on(C.CREDIT, onCredit);

    Event.on("deposit_archive", showArchive);
    wait(400).then(() => loadCoinId(initialCoinRef.current));

    return () => {
      socket.off(C.CREDIT, onCredit);
      if (Event.off) Event.off("deposit_archive", showArchive);
    };
  }, [getCreditAndCoins, loadCoinId, showArchive]);

  // Dropdown outside click (optional – your original had a class-based approach)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen && !event.target.closest('.dropdown-container')) {
        setIsOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isOpen]);

  // Check eligibility on mount and when needed
  useEffect(() => {
    checkWagerEligibility();
    Event.on && Event.on('coin_changed', checkWagerEligibility);
    return () => {
      Event.off && Event.off('coin_changed', checkWagerEligibility);
    };
  }, [checkWagerEligibility]);

  /* ----------------- Derived lists ----------------- */
  const filteredCoins = coins
    .filter(coin => {
      if (coin.preffix === 'BJT' || coin.preffix === 'SC') return false;
      return coin.preffix.toLowerCase().includes(searchTerm.toLowerCase());
    })
    .sort((a, b) => {
      if (a.preffix === 'BTC') return -1;
      if (b.preffix === 'BTC') return 1;
      return 0;
    });

  const selectedCoinData = coins.find(coin => coin.preffix === selectedCoin);

  /* ----------------- Handlers ----------------- */
  const handleCoinSelect = async (coinName) => {
    if (storage.getKey('coin') === coinName) {
      setIsOpen(false);
      return;
    }
    storage.setKey('coin', coinName);
    const credit = forceSatoshiFormat(credits[__.lowerCase(coinName)] || 0);
    storage.setKey('credit', credit);
    setSelectedCoin(coinName);
    setIsOpen(false);
    try {
      await wait(200);
      Event.emit('coin_changed', coinName);
    } catch (error) {
      console.error('Error during coin selection:', error);
    }
  };

  /* ----------------- Render ----------------- */
  return (
    <Container>
      <div style={{ display: "flex", gap: "5px", width: "100%" }}>
        <div style={{ flex: '1' }}>
          <div style={{ color: "#B3BEC1", margin: "4px 0" }}>Deposit Currency</div>
          <DropdownContainer className="dropdown-container">
            <DropdownButton onClick={(e) => { e.stopPropagation(); setIsOpen(!isOpen); }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <CoinImage src={`/assets/images/${selectedCoinData?.image}`} alt={selectedCoin} />
                <CoinName>{selectedCoin}</CoinName>
              </div>
              <ChevronDown size={20} />
            </DropdownButton>

            {isOpen && (
              <DropdownPanel onClick={e => e.stopPropagation()}>
                <SearchContainer>
                  <SearchIcon>
                    <Search size={20} />
                  </SearchIcon>
                  <SearchInput
                    placeholder="Search coins..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    autoFocus
                  />
                </SearchContainer>

                {filteredCoins.map((coin) => (
                  <CoinOption
                    key={coin.id || coin.preffix}
                    onClick={() => handleCoinSelect(coin.preffix)}
                    isSelected={selectedCoin === coin.preffix}
                  >
                    <CoinImage src={`/assets/images/${coin.image}`} alt={coin.preffix} />
                    <CoinName>{coin.preffix}</CoinName>
                    <CoinBalance>
                      {forceSatoshiFormat(credits[__.lowerCase(coin.preffix)] || 0)}
                    </CoinBalance>
                  </CoinOption>
                ))}
              </DropdownPanel>
            )}
          </DropdownContainer>
        </div>
      </div>

      <div style={{ flex: "1", width: "100%" }}>
        <div style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ color: "#B3BEC1", margin: "4px 0" }}>Withdrawl Amount</div>
        </div>
      </div>

      <div style={{ width: "100%" }}>
        <Input
          value={state.amount}
          onChange={(e) => {
            const newAmount = e.target.value;
            setState((prev) => ({ ...prev, amount: newAmount }));
            setError3("");
            if (selectedCoin === "MVR") {
              const amount = parseFloat(newAmount);
              if (newAmount && !isNaN(amount)) {
                if (amount < 500) setError3("Minimum withdrawal amount for MVR is 500");
                else if (amount > 50000) setError3("Maximum withdrawal amount for MVR is 50,000");
              }
            }
          }}
          placeholder={placeholder}
        />
        {error3 && (
          <div style={{ color: "red", fontSize: "12px", marginTop: "4px", marginBottom: "8px" }}>
            {error3}
          </div>
        )}
      </div>

      {!isFiatCurrency() && renderCryptoForm()}
      {isFiatCurrency() && renderBankForm()}

      <WithdrawContainer>
        <Row1>
          <Label1 isGreen>Withdraw amount</Label1>
          <Amount isGreen large bold>
            {parseFloat(state.amount || 0).toFixed(isFiatCurrency() ? 2 : 8)} {selectedCoin}
          </Amount>
        </Row1>

        {!isFiatCurrency() && (
          <Row1>
            <Label1 isGray>
              Fee (1%):
              <IconButton><FileQuestion /></IconButton>
            </Label1>
            <Amount>
              {(parseFloat(state.amount || 0) * 0.01).toFixed(8)} {selectedCoin}
            </Amount>
          </Row1>
        )}

        <Row1>
          <Label1 isGray>Total Withdraw amount</Label1>
          <Amount>
            {isFiatCurrency()
              ? (parseFloat(state.amount || 0)).toFixed(2)
              : (parseFloat(state.amount || 0) * 0.99).toFixed(8)
            } {selectedCoin}
          </Amount>
        </Row1>
      </WithdrawContainer>

      {/* User-facing message about eligibility */}
      {!eligible && (
        <div style={{ color: '#FFCC00', margin: '8px 0', fontSize: 13 }}>
          Withdrawals are disabled for your account. {wagerInfo?.wager !== undefined && `Wager: ${wagerInfo.wager}.`} Please complete wagering/deposit requirements or contact support.
        </div>
      )}

      {/* DepositButton — ALWAYS clickable. If not eligible it opens modal. */}
      <DepositButton
        onClick={onWithdrawButtonClick}
        role="button"
        aria-disabled={checkingEligibility || loading}
        style={{
          // Deliberately NOT disabling pointerEvents when not eligible. Only dim if loading/checking.
          opacity: (checkingEligibility || loading) ? 0.6 : 1,
          cursor: (checkingEligibility || loading) ? 'not-allowed' : 'pointer'
        }}
      >
        {checkingEligibility ? "Checking..." : (loading ? "Processing..." : (isFiatCurrency() ? "Withdraw" : (final ? "Confirm Withdrawal" : "Preview")))}
      </DepositButton>

      {/* Confirmation Modal for Crypto Withdrawals */}
      {final && !isFiatCurrency() && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex',
          justifyContent: 'center', alignItems: 'center', zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#1E2329', padding: '24px', borderRadius: '8px',
            maxWidth: '500px', width: '90%'
          }}>
            <h3 style={{ color: 'white', marginBottom: '16px' }}>Confirm Withdrawal</h3>

            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: '#B3BEC1' }}>Address:</span>
                <span style={{ color: 'white', wordBreak: 'break-all' }}>{withdrawAddress}</span>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: '#B3BEC1' }}>Network:</span>
                <span style={{ color: 'white' }}>{selectedCoin}</span>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: '#B3BEC1' }}>Chain:</span>
                <span style={{ color: 'white' }}>{chainName}</span>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: '#B3BEC1' }}>Amount:</span>
                <span style={{ color: 'white' }}>{parseFloat(state.amount).toFixed(8)} {selectedCoin}</span>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: '#B3BEC1' }}>Fee (1%):</span>
                <span style={{ color: 'white' }}>{(parseFloat(state.amount) * 0.01).toFixed(8)} {selectedCoin}</span>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: '#B3BEC1' }}>You will receive:</span>
                <span style={{ color: '#0ECB81' }}>{(parseFloat(state.amount) * 0.99).toFixed(8)} {selectedCoin}</span>
              </div>

              <div style={{ marginTop: '16px' }}>
                <span style={{ color: '#B3BEC1', display: 'block', marginBottom: '8px' }}>
                  Enter your password to confirm withdrawal
                </span>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  style={{
                    width: '100%',
                    backgroundColor: '#2B3139',
                    border: '1px solid #424B52',
                    padding: '10px 12px',
                    borderRadius: '4px',
                    color: 'white'
                  }}
                />
                {passwordError && (
                  <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
                    {passwordError}
                  </div>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={handleCancelWithdraw}
                style={{
                  backgroundColor: 'transparent', border: '1px solid #B3BEC1',
                  color: '#B3BEC1', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmWithdraw}
                style={{
                  backgroundColor: '#0ECB81', border: 'none', color: 'white',
                  padding: '8px 16px', borderRadius: '4px', cursor: 'pointer'
                }}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Wager requirement modal (shown when not eligible and user clicks withdraw) */}
      {showWagerModal && (
        <ModalOverlay onClick={closeWagerModal}>
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Wager Requirement Incomplete</ModalTitle>
              <button onClick={closeWagerModal} style={{ background: 'transparent', border: 'none', color: '#ccc', fontSize: 20, cursor: 'pointer' }}>×</button>
            </ModalHeader>

            <ModalBody>
              <p style={{ marginTop: 8, color: '#B3BEC1', fontSize: 14 }}>
                Your current wager is lower than the required amount for withdrawals. Increase your wagering or deposit to unlock withdrawals.
              </p>

              <div style={{ display: 'flex', gap: 12, marginTop: 14 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ color: '#999', fontSize: 13 }}>Current wager</div>
                  <div style={{ marginTop: 6, fontWeight: 600, fontSize: 16 }}>{(wagerInfo?.wager ?? 0).toLocaleString()}</div>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ color: '#999', fontSize: 13 }}>Required wager</div>
                  <div style={{ marginTop: 6, fontWeight: 600, fontSize: 16 }}>{(wagerInfo?.required ?? 0).toLocaleString()}</div>
                </div>
              </div>

              <div style={{ marginTop: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <div style={{ color: '#B3BEC1', fontSize: 13 }}>Progress to required wager</div>
                  <div style={{ color: '#fff', fontWeight: 600, fontSize: 13 }}>{progressPercent}%</div>
                </div>

                <ProgressTrack>
                  <ProgressFill widthPercent={progressPercent} />
                </ProgressTrack>
              </div>

              <div style={{ marginTop: 18, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button
                  onClick={closeWagerModal}
                  style={{
                    background: 'transparent',
                    border: '1px solid #3b3f42',
                    color: '#B3BEC1',
                    padding: '8px 12px',
                    borderRadius: 8,
                    cursor: 'pointer'
                  }}
                >
                  Close
                </button>
                {/* <button
                  onClick={onModalDepositCTA}
                  style={{
                    background: '#FFCC00',
                    border: 'none',
                    padding: '8px 12px',
                    borderRadius: 8,
                    cursor: 'pointer',
                    fontWeight: 700
                  }}
                >
                  Deposit to meet requirement
                </button> */}
              </div>
            </ModalBody>
          </ModalCard>
        </ModalOverlay>
      )}

      {showTwoFactorInput && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex',
          justifyContent: 'center', alignItems: 'center', zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#1E2329', padding: '24px', borderRadius: '8px',
            maxWidth: '500px', width: '90%'
          }}>
            <h3 style={{ color: 'white', marginBottom: '16px' }}>Two-Factor Authentication</h3>
            <div style={{ marginBottom: '16px' }}>
              <span style={{ color: '#B3BEC1', display: 'block', marginBottom: '8px' }}>
                Enter your 2FA verification code
              </span>
              <input
                type="text"
                value={twoFactorCode}
                onChange={(e) => setTwoFactorCode(e.target.value)}
                placeholder="Enter verification code"
                style={{
                  width: '100%', backgroundColor: '#2B3139', border: '1px solid #424B52',
                  padding: '10px 12px', borderRadius: '4px', color: 'white'
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowTwoFactorInput(false)}
                style={{
                  backgroundColor: 'transparent', border: '1px solid #B3BEC1',
                  color: '#B3BEC1', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleTwoFactorVerification}
                style={{
                  backgroundColor: '#0ECB81', border: 'none', color: 'white',
                  padding: '8px 16px', borderRadius: '4px', cursor: 'pointer'
                }}
              >
                Verify
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedCoin==="MVR" && (
        <WarningTextGreen>
          Note: Total 2 withdrawals per day, Unlimited withdrawal players can swap MVR to Crypto
        </WarningTextGreen>
      )}

      <WarningContainer>
        <WarningText>
          {isFiatCurrency()
            ? "For security purposes, large or suspicious withdrawal may take 1-6 hours for audit process. We appreciate your patience!"
            : `Please ensure you're using the correct ${state.selectedNetwork} network for your ${selectedCoin} withdrawal. Sending to an incorrect network may result in permanent loss of funds.`
          }
        </WarningText>
      </WarningContainer>
    </Container>
  );
};

export default SkylaWithdraw;
